
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// types
import {
    CalculationT,
    TranscriptCalculationT,
    TranscriptT,
    UpdateTranscriptPayload,
} from '@/core/types/billing.types'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useDebounce } from 'vue-composable'

// realisations
import { billing } from '@/core/realisations/billing.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Loader,
        TheInput,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const notifications = useNotifications()

        const transcript_id = Number(route.params.id)

        const state = reactive({
            is_loading: false,
            is_failed: true,
            transcript: null as TranscriptT | null,
            calculations: [] as TranscriptCalculationT[],
            is_submit_loading: false,
            turnoverDate: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                turnoverDate: { required },
            },
            state
        )

        const search_state = reactive({
            query: null as string | null,
            calculations: [] as CalculationT[],
        })

        const loadTranscript = async () => {
            try {
                state.is_loading = true

                const response = await billing.getTranscript(transcript_id)

                if (response.data.company) {
                    const companyId = response.data.company?.id
                    const statusId = 1
                    const calculations_response = await billing.getCalculations({
                        page: 1,
                        limit: 9999,
                        payerCompanyId: companyId,
                        statusId,
                    })

                    search_state.calculations = calculations_response.data
                }

                state.transcript = response.data
                state.calculations = [...response.data.calculations]

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении транскрипта произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadTranscript)

        const onRouteBack = () => {
            router.push('/billing/transcripts')
        }

        const onRemoveCalculation = (calculation: CalculationT) => {
            const answer = confirm(
                `Вы действительно хотите удалить вычисление ${calculation.invoiceNumber} из транскрипта?`
            )
            if (!answer) return

            const calculation_index = state.calculations.findIndex(
                (calc) => calc.id === calculation.id
            )
            if (calculation_index < 0) return

            state.calculations.splice(calculation_index, 1)
            search_state.calculations.unshift({ ...calculation })
        }

        const onAddCalculation = (calculation: CalculationT) => {
            const calculation_index = search_state.calculations.findIndex(
                (calc) => calc.id === calculation.id
            )
            if (calculation_index < 0) return

            state.calculations.push({ ...calculation })
            search_state.calculations.splice(calculation_index, 1)
        }

        const addAll = () => {
            search_state.calculations.forEach((calculation) => {
                state.calculations.push({ ...calculation })
            })
            search_state.calculations = []
        }

        const isCalculationAlreadyAdded = (calculation_id: number) => {
            const calculation = state.calculations.find((calc) => calc.id === calculation_id)
            return Boolean(calculation)
        }

        const isNotHaveActNumber = computed(() => {
            if (state.transcript) {
                if (state.transcript.actNumber) {
                    return false
                }
            }

            return true
        })

        const onSubmit = async () => {
            if (state.is_submit_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_submit_loading = true

                const payload: UpdateTranscriptPayload = {
                    calculationIds: state.calculations.map((calculation) => calculation.id),
                    turnoverDate: state.turnoverDate as string,
                }

                await billing.updateTranscript(transcript_id, payload)

                notifications.notify({ title: 'Транскрипт сохранен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании транскрипта произошла ошибка',
                })
            } finally {
                state.is_submit_loading = false
            }
        }

        const renderedCalculations = computed(() => {
            if (search_state.query) {
                return search_state.calculations.filter((calc) => {
                    return calc.invoiceNumber
                        .toLowerCase()
                        .includes((search_state.query as string).toLowerCase())
                })
            } else {
                return search_state.calculations
            }
        })

        return {
            transcript_id,
            state,
            state_form$,
            search_state,
            //
            loadTranscript,
            onRouteBack,
            onRemoveCalculation,
            onAddCalculation,
            isCalculationAlreadyAdded,
            onSubmit,
            addAll,
            //
            isNotHaveActNumber,
            renderedCalculations,
        }
    },
})
